import { useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import {
  BpView,
  buildBpContentHtml,
} from '@/components/Approval/@components/BusinessPartnerRegistration';
import EyeIcon from '@/public/icons/approval/eye.svg';
import {
  ACCOUNTING_BP_ACCOUNT_NOTICE,
  ACCOUNTING_BP_TYPE,
  hasAccountChangedNotice,
  isAccountingBpRegister,
  parseLegacySapLink,
  withAccountingBpRequestType,
  type AccountingBpState,
} from '@/utils/approval/accountingBusinessPartner';
import {
  normalizeBpState,
  type BpFormState,
} from '@/utils/approval/businessPartnerRegistration';
import { openSapVoucherLink } from '@/utils/approval/saVoucher';
import { AccountingBpLinkButton, AccountingBpRoot } from './styles';

type SapMarkupProps = {
  state: Pick<AccountingBpState, 'companyName' | 'partners'>;
  t: TFunction;
};

/** 레거시 #eas_bp_content 의 회사명·거래처 표(id 동일). 「보기」는 링크만 data 속성으로 */
const SapMarkup = ({ state, t }: SapMarkupProps) => (
  <>
    <div className="form-group">
      <span>{t('회사명')} : </span>
      <span id="sap_company_text">{state.companyName}</span>
    </div>
    <table id="bpTable">
      <thead>
        <tr>
          <th>{t('순번')}</th>
          <th>{t('신청유형')}</th>
          <th>{t('사업자번호(주민번호)')}</th>
          <th>{t('비즈니스파트너명')}</th>
          <th>{t('대표자명')}</th>
          <th>{t('보기')}</th>
        </tr>
      </thead>
      <tbody>
        {state.partners.map((p, i) => (
          <tr key={`${p.seq}-${i}`}>
            <td className="text-center">{p.seq}</td>
            <td className="text-center">{p.requestType}</td>
            <td className="text-center">{p.taxNumber}</td>
            <td className="text-center">{p.name}</td>
            <td className="text-center">{p.representative}</td>
            <td className="text-center">
              {p.sapLink && <a data-sap-link={p.sapLink}>{t('보기')}</a>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {hasAccountChangedNotice(state.partners) && (
      <p className="account-change-notice">{t(ACCOUNTING_BP_ACCOUNT_NOTICE)}</p>
    )}
  </>
);

export const buildAccountingBpSapHtml = (
  state: SapMarkupProps['state'],
  t: TFunction,
): string => renderToStaticMarkup(<SapMarkup state={state} t={t} />);

/** attributes.content — 회사명·거래처 표 + '생성'이면 업체소싱정보 */
export const buildAccountingBpContentHtml = (
  state: AccountingBpState,
  form: BpFormState | null,
  t: TFunction,
): string => {
  const sap = buildAccountingBpSapHtml(state, t);
  if (!form || !isAccountingBpRegister(state.partners)) return sap;
  return (
    sap +
    buildBpContentHtml(
      normalizeBpState(withAccountingBpRequestType(form, state.partners)),
      ACCOUNTING_BP_TYPE,
      t,
    )
  );
};

/** 저장 content(레거시 문서 포함) 그대로 — 「보기」만 SAP 새 창 버튼으로 */
export const AccountingBpView = ({ content }: { content: string }) => {
  const { t } = useTranslation();
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      if (tagName !== 'a') return undefined;
      const link =
        element.getAttribute('data-sap-link') ||
        parseLegacySapLink(element.getAttribute('onclick'));
      if (!link) return null;
      return (
        <AccountingBpLinkButton
          key={props.key as string}
          type="button"
          aria-label={t('보기')}
          onClick={() => openSapVoucherLink(link)}
        >
          <EyeIcon width={16} height={16} />
        </AccountingBpLinkButton>
      );
    },
    [t],
  );
  return (
    <AccountingBpRoot>
      <BpView content={content} transform={transform} />
    </AccountingBpRoot>
  );
};
